import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../utils/trpc';
import EditIcon from '../images/EditIcon.png';
import TagManagerModal from './TagManagerModal';
import styles from '../styles/GroupCard.module.css';
import { supabase } from '../utils/supabase';

interface Tag {
  id: number;
  name: string;
}

interface VenueCardProps {
  venue: {
    venue_id: string;
    venue_name: string;
    address?: string;
    banner_url?: string;
  };
}

const VenueCard = ({ venue }: VenueCardProps) => {
  const navigate = useNavigate();
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [galleryUrls, setGalleryUrls] = useState<string[]>([]);

  // Fetch tags
  const { data: venueTags, refetch: refetchVenueTags } = trpc.tags.getVenueTags.useQuery(
    { venueId: venue.venue_id },
    { enabled: !!venue.venue_id, staleTime: 0 },
  );

  const currentTags: Tag[] = useMemo(() => {
    if (!venueTags || !Array.isArray(venueTags)) return [];
    return venueTags.map((entry: any) => ({
      id: entry.tag_id,
      name: entry.tags?.name ?? 'Unknown',
    }));
  }, [venueTags]);

  const handleModalClose = async () => {
    setIsTagModalOpen(false);
    await refetchVenueTags();
  };

  // Fetch gallery images
  const { data: galleryImages } = trpc.venue.getImages.useQuery(
    { venue_id: venue.venue_id },
    { enabled: !!venue.venue_id },
  );

  useEffect(() => {
    if (!galleryImages) {
      setGalleryUrls([]);
      return;
    }
    const urls = galleryImages
      .map((img: any) => {
        const { data } = supabase.storage.from('venues').getPublicUrl(img.storage_path);
        return data?.publicUrl ? `${data.publicUrl}?t=${Date.now()}` : '';
      })
      .filter(Boolean);
    setGalleryUrls(urls);
  }, [galleryImages]);

  return (
    <div className={styles.cardContainer}>
      <div className={styles.card}>
        {/* Hero Banner */}
        {venue.banner_url ? (
          <div className={styles.bannerContainer}>
            <img
              onLoad={(e) => e.currentTarget.classList.add('loaded')}
              src={venue.banner_url}
              alt="Venue Banner"
              className={styles.bannerImage}
            />
          </div>
        ) : (
          <div className={styles.heroBanner} />
        )}
        <div className={styles.cardContent}>
          {/* Group Info */}
          <div className={styles.cardInfo}>
            <div className={styles.header}>
              <h3 className={styles.name}>{venue.venue_name}</h3>
            </div>

            <p className={styles.address}>{venue.address || 'No address provided'}</p>

            {/* Gallery */}
            <div className={styles.gallerySection}>
              <h2 className={styles.galleryTitle}>Gallery</h2>
              {galleryUrls.length > 0 ? (
                <div className={styles.gallery}>
                  {galleryUrls.map((url, index) => (
                    <div key={index} className={styles.galleryItem}>
                      <img
                        onLoad={(e) => e.currentTarget.classList.add('loaded')}
                        src={url}
                        alt="Venue Gallery"
                        className={styles.galleryImage}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyGallery}>
                  <span className={styles.emptyIcon}>🖼️</span>
                  <p className={styles.emptyText}>No photos yet</p>
                </div>
              )}
            </div>

            {/* Tags */}
            <div className={styles.tagsSection}>
              <div className={styles.sectionHeaderContainer}>
                <h3 className={styles.sectionHeader}>Interests</h3>
                <button
                  data-testid="editTagsButton"
                  className={styles.editTagsButton}
                  onClick={() => setIsTagModalOpen(true)}
                >
                  <img
                    onLoad={(e) => e.currentTarget.classList.add('loaded')}
                    src={EditIcon}
                    alt="Edit"
                    className={styles.editIcon}
                  />
                </button>
              </div>

              <div className={styles.tagsContainer}>
                {currentTags.length === 0 ? (
                  <p>No interests added yet.</p>
                ) : (
                  currentTags.map((tag) => (
                    <span key={tag.id} className={styles.tag}>
                      {tag.name}
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className={styles.cardActions}>
            <button
              className={styles.viewBtn}
              onClick={() => navigate(`/venues/${venue.venue_name}`)}
            >
              View
            </button>

            <button
              className={styles.editBtn}
              onClick={() => navigate(`/venues/${venue.venue_id}/edit`)}
            >
              Edit
            </button>
          </div>
        </div>
      </div>

      <TagManagerModal
        isOpen={isTagModalOpen}
        onClose={handleModalClose}
        initialTags={currentTags}
        originalTags={currentTags}
        mode="edit"
        min={1}
        max={10}
        profileType="venue_owner"
        venueId={venue.venue_id}
      />
    </div>
  );
};

export default VenueCard;
